// ============================================================
// useCaseAccess.ts
// Does the current user have access to these linked cases? Drives the LOCKED
// case-link UX (CaseLink): an inaccessible link renders greyed + locked +
// non-clickable instead of navigating into a "no access" dead end.
//
// One module-level cache shared by every consumer, so many links on a page
// cost ONE batched request (ids requested in the same tick are coalesced into
// a single POST /api/cases/access-check). Entries expire after 60s so a newly
// granted/revoked case settles without a reload.
// Unknown (still loading / request failed) → 'unknown', which renders as a
// normal link; the server-side guard + access-denied toast still apply.
// ============================================================
import { useEffect, useState } from 'react'
import { checkCaseAccessApi } from '../api/casevoApi'

// 'unknown' = not checked yet / in flight. Consumers render it NEUTRAL (never
// as a clickable link), so a link can't flash clickable and then lock.
export type CaseAccessState = 'unknown' | 'allowed' | 'locked'

const TTL_MS = 60_000
const cache = new Map<string, { ok: boolean; at: number }>()
const inflight = new Set<string>()
let queued = new Set<string>()
let timer: ReturnType<typeof setTimeout> | null = null
const listeners = new Set<() => void>()

const notify = (): void => { listeners.forEach((fn) => fn()) }

const flush = (): void => {
  timer = null
  const ids = Array.from(queued)
  queued = new Set()
  if (ids.length === 0) return
  ids.forEach((id) => inflight.add(id))
  checkCaseAccessApi(ids)
    .then((accessible) => {
      const ok = new Set(accessible)
      const now = Date.now()
      ids.forEach((id) => cache.set(id, { ok: ok.has(id), at: now }))
    })
    .catch(() => {
      // Check failed → fall back to a normal link (the server guard + the
      // access-denied toast still apply); never leave a link stuck neutral.
      const now = Date.now()
      ids.forEach((id) => cache.set(id, { ok: true, at: now - TTL_MS + 5_000 }))
    })
    .finally(() => { ids.forEach((id) => inflight.delete(id)); notify() })
}

const request = (id: string): void => {
  const hit = cache.get(id)
  if (hit && Date.now() - hit.at < TTL_MS) return
  if (inflight.has(id) || queued.has(id)) return
  queued.add(id)
  if (!timer) timer = setTimeout(flush, 20)
}

// Seed the cache from data that already carries the answer (e.g. the
// appointment payload's caseAccessible) so the FIRST render is correct —
// no request, no neutral beat, no flash.
export const primeCaseAccess = (id: string | null | undefined, ok: boolean | null | undefined): void => {
  if (!id || typeof ok !== 'boolean') return
  cache.set(id, { ok, at: Date.now() })
}

export const caseAccessOf = (id: string | null | undefined): CaseAccessState => {
  if (!id) return 'unknown'
  const hit = cache.get(id)
  if (!hit) return 'unknown'
  return hit.ok ? 'allowed' : 'locked'
}

// Subscribe to the access state of `ids`; re-renders when results arrive.
export const useCaseAccess = (ids: Array<string | null | undefined>): ((id: string | null | undefined) => CaseAccessState) => {
  const [, setTick] = useState(0)
  const key = ids.filter(Boolean).join(',')
  useEffect(() => {
    const fn = (): void => setTick((t) => t + 1)
    listeners.add(fn)
    key.split(',').filter(Boolean).forEach(request)
    return () => { listeners.delete(fn) }
  }, [key])
  return caseAccessOf
}
